import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { creditsService } from '@/services/apiClient';
import type { CreditFilters, CreditStatusUpdate } from '@/types/credits';

/**
 * Credits Hooks
 * React Query hooks for /api/v1/credits/* and /api/v1/credit-payments/* endpoints
 */

// Query keys
export const creditKeys = {
  all: ['credits'] as const,
  lists: () => [...creditKeys.all, 'list'] as const,
  list: (filters: CreditFilters) => [...creditKeys.lists(), filters] as const,
  detail: (id: string) => [...creditKeys.all, 'detail', id] as const,
  byUser: (userId: string, organizationId?: string) =>
    [...creditKeys.all, 'user', userId, organizationId ?? null] as const,
  payments: (userId: string) => [...creditKeys.all, 'payments', userId] as const,
};

/**
 * Hook to fetch credits with filters (paginated)
 */
export const useCredits = (filters: CreditFilters = {}) => {
  return useQuery({
    queryKey: creditKeys.list(filters),
    queryFn: () => creditsService.getCredits(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch credits for a specific user
 */
export const useCreditsByUser = (userId: string | undefined, organizationId?: string) => {
  return useQuery({
    queryKey: creditKeys.byUser(userId || '', organizationId),
    queryFn: () => creditsService.getCreditsByUser(userId || '', organizationId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single credit with all relationships
 */
export const useCredit = (creditId: string) => {
  return useQuery({
    queryKey: creditKeys.detail(creditId),
    queryFn: () => creditsService.getCredit(creditId),
    enabled: !!creditId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch credit payments for a specific user
 */
export const useCreditPayments = (userId: string | undefined) => {
  return useQuery({
    queryKey: creditKeys.payments(userId || ''),
    queryFn: () => creditsService.getCreditPayments(userId || ''),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to approve a credit
 */
export const useApproveCredit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (creditId: string) => creditsService.approveCredit(creditId),
    onSuccess: () => {
      // Invalidate all credit queries to refetch data
      void queryClient.invalidateQueries({ queryKey: creditKeys.all });
    },
  });
};

/**
 * Hook to update credit status
 */
export const useUpdateCreditStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      creditId,
      statusUpdate,
    }: {
      creditId: string;
      statusUpdate: CreditStatusUpdate;
    }) => creditsService.updateCreditStatus(creditId, statusUpdate),
    onSuccess: () => {
      // Invalidate all credit queries to refetch data
      void queryClient.invalidateQueries({ queryKey: creditKeys.all });
    },
  });
};
